import { db } from './database';
import { normalizePhilippineMobile } from '../domain/customers/customerUtils';
import { notifyOutboxChanged } from '../services/sync/syncEngine';
import type {
  AddOnRecord,
  CustomerRecord,
  ServiceRecord,
  ServiceItemRecord,
  SettingRecord,
  ShiftRecord,
  SyncMetadataRecord,
  CreateOrderInput,
} from './types';

export const orderRepository = {
  getById: (id: string) => db.orders.get(id),
  getByOrderNumber: (orderNumber: string) =>
    db.orders.where('orderNumber').equals(orderNumber).first(),
  getByIdempotencyKey: (key: string) => db.orders.where('idempotencyKey').equals(key).first(),
  list: () => db.orders.orderBy('createdAt').reverse().toArray(),
  listReady: () => db.orders.where('orderStatus').equals('READY').reverse().sortBy('createdAt'),
  search: async (term: string) => {
    const query = term.trim().toLowerCase();
    if (!query) return db.orders.orderBy('createdAt').reverse().toArray();
    const [orders, customers] = await Promise.all([db.orders.toArray(), db.customers.toArray()]);
    const normalized = normalizePhilippineMobile(term);
    const customerIds = new Set(
      customers
        .filter(
          (c) =>
            c.name.toLowerCase().includes(query) || (normalized && c.mobileNumber === normalized),
        )
        .map((c) => c.id),
    );
    return orders
      .filter(
        (o) =>
          o.orderNumber.toLowerCase().includes(query) ||
          (o.claimCode ?? '').toLowerCase() === query ||
          (o.customerId && customerIds.has(o.customerId)),
      )
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  },
};

export const orderItemRepository = {
  listByOrderId: (orderId: string) => db.orderItems.where('orderId').equals(orderId).toArray(),
};

export const customerRepository = {
  getById: (id: string) => db.customers.get(id),
  findByMobileNumber: (mobileNumber: string) =>
    db.customers.where('mobileNumber').equals(mobileNumber).first(),
  put: (customer: CustomerRecord) => db.customers.put(customer),
  list: () => db.customers.orderBy('updatedAt').reverse().toArray(),
  search: async (term: string) => {
    const query = term.trim().toLowerCase();
    if (!query) return db.customers.orderBy('updatedAt').reverse().toArray();
    const normalized = normalizePhilippineMobile(term);
    return (await db.customers.toArray())
      .filter(
        (c) =>
          c.name.toLowerCase().includes(query) ||
          (c.customerCode ?? '').toLowerCase().includes(query) ||
          (!!normalized && c.mobileNumber === normalized),
      )
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  },
};

export const paymentRepository = {
  listByOrderId: (orderId: string) => db.payments.where('orderId').equals(orderId).toArray(),
};

export const serviceRepository = {
  getById: (id: string) => db.services.get(id),
  listActive: async () =>
    (await db.services.orderBy('sortOrder').toArray()).filter(
      (service) => service.active !== false,
    ),
  put: (service: ServiceRecord) => db.services.put(service),
};

export const serviceItemRepository = {
  getById: (id: string) => db.serviceItems.get(id),
  listByServiceId: async (serviceId: string) =>
    (await db.serviceItems.where('serviceId').equals(serviceId).sortBy('sortOrder')).filter(
      (item) => item.active !== false,
    ),
  put: (item: ServiceItemRecord) => db.serviceItems.put(item),
};

export const addonRepository = {
  getById: (id: string) => db.addons.get(id),
  listActive: async () =>
    (await db.addons.orderBy('sortOrder').toArray()).filter((addon) => addon.active !== false),
  put: (addon: AddOnRecord) => db.addons.put(addon),
};

export const outboxRepository = {
  getByIdempotencyKey: (key: string) => db.outbox.where('idempotencyKey').equals(key).first(),
  listPending: () => db.outbox.where('status').equals('PENDING').sortBy('createdAt'),
};

export const auditLogRepository = {
  listForEntity: (entityId: string) =>
    db.auditLogs.where('entityId').equals(entityId).sortBy('createdAt'),
};

export const settingsRepository = {
  get: (key: string) => db.settings.get(key),
  put: (setting: SettingRecord) => db.settings.put(setting),
};

export const syncMetadataRepository = {
  get: (key: string) => db.syncMetadata.get(key),
  put: (metadata: SyncMetadataRecord) => db.syncMetadata.put(metadata),
};

export const shiftRepository = {
  getById: (id: string) => db.shifts.get(id),
  getOpenByCashier: (cashierId: string) =>
    db.shifts
      .where('cashierId')
      .equals(cashierId)
      .filter((shift) => shift.status === 'OPEN')
      .first(),
  put: (shift: ShiftRecord) => db.shifts.put(shift),
};

async function ensureUniqueOrder(input: CreateOrderInput): Promise<void> {
  const [existingId, existingOrderNumber, existingIdempotency] = await Promise.all([
    db.orders.get(input.order.id),
    db.orders.where('orderNumber').equals(input.order.orderNumber).first(),
    db.orders.where('idempotencyKey').equals(input.order.idempotencyKey).first(),
  ]);

  if (existingId || existingOrderNumber || existingIdempotency) {
    throw new Error(`Duplicate order prevented: ${input.order.orderNumber}`);
  }
}

async function putOrderGraph(input: CreateOrderInput): Promise<void> {
  await ensureUniqueOrder(input);
  await db.orders.add(input.order);
  if (input.items.length > 0) {
    await db.orderItems.bulkAdd(input.items);
  }
  if (input.customer) {
    await db.customers.put(input.customer);
  }
  if (input.payment) {
    await db.payments.add(input.payment);
  }
  await db.auditLogs.add(input.audit);
  await db.outbox.add(input.outbox);
}

export async function createOrderAtomically(input: CreateOrderInput): Promise<void> {
  await db.transaction(
    'rw',
    [db.orders, db.orderItems, db.customers, db.payments, db.auditLogs, db.outbox],
    async () => {
      await putOrderGraph(input);
    },
  );
  notifyOutboxChanged();
}
